import { useState, useEffect } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import BottomNavigation from '@/components/BottomNavigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, DollarSign, TrendingUp, Wallet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import WithdrawalForm from '@/components/WithdrawalForm';
import WithdrawalHistory from '@/components/WithdrawalHistory';

interface Profile {
  id: string;
  available_earnings: number;
  role: 'customer' | 'vendor' | 'driver' | 'admin';
}

const EarningsPage = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showWithdrawalForm, setShowWithdrawalForm] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    fetchProfile();
  }, [user?.id]);

  const fetchProfile = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, available_earnings, role')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
      } else {
        setProfile(data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = () => {
    const earnings = profile?.available_earnings || 0;
    if (earnings < 10) {
      toast({
        title: "Withdrawal Not Available",
        description: "You need a minimum of D10 to withdraw earnings.",
        variant: "destructive",
      });
      return;
    }

    setShowWithdrawalForm(true);
  };

  const handleWithdrawalSuccess = () => {
    fetchProfile(); // Refresh the profile to update earnings
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  const canWithdraw = (profile?.available_earnings || 0) >= 10;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">Earnings</h1>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6">
        {/* Earnings Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              Available Earnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">
                D{(profile?.available_earnings || 0).toFixed(2)}
              </div>
              <p className="text-gray-600 text-sm">
                Earn 5% commission from orders made through your posts
              </p>
            </div>
          </CardContent>
        </Card>

        {/* How It Works */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              How Earnings Work
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                1
              </div>
              <div>
                <p className="font-medium">Post Content</p>
                <p className="text-sm text-gray-600">Share photos/videos of products from restaurants or marketplace vendors</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                2
              </div>
              <div>
                <p className="font-medium">Users Order from Your Post</p>
                <p className="text-sm text-gray-600">When someone adds items to cart from your post and completes checkout</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                3
              </div>
              <div>
                <p className="font-medium">Earn 5% Commission</p>
                <p className="text-sm text-gray-600">Get 5% of the total order value added to your earnings</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Withdrawal */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Withdraw Earnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Minimum withdrawal:</strong> D10
                </p>
                <p className="text-sm text-yellow-800 mt-1">
                  {canWithdraw 
                    ? "You can withdraw your earnings now!" 
                    : `You need D${(10 - (profile?.available_earnings || 0)).toFixed(2)} more to withdraw.`
                  }
                </p>
              </div>
              
              <Button
                onClick={handleWithdraw}
                disabled={!canWithdraw}
                className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                {canWithdraw ? 'Withdraw Earnings' : 'Withdrawal Not Available'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Withdrawal History */}
        {user?.id && <WithdrawalHistory userId={user.id} />}
      </div>

      {/* Withdrawal Form */}
      {user?.id && (
        <WithdrawalForm
          isOpen={showWithdrawalForm}
          onClose={() => setShowWithdrawalForm(false)}
          onSuccess={handleWithdrawalSuccess}
          availableEarnings={profile?.available_earnings || 0}
          userId={user.id}
        />
      )}

      <BottomNavigation />
    </div>
  );
};

export default EarningsPage;